/**
 * unitCodeMatch — how one stretch of pasted text is read as a unit code.
 *
 * Shared by the parser (unitCodeParser) and the shorthand helpers
 * (unitCodeShorthand) so every reader classifies a match the same way. App is the
 * source; Sales vendors these files byte-for-byte (hash-pinned), with the shared
 * fixtures in unitCodeParser.fixtures.json.
 */

/**
 * Shorthand with no suffix: `1re1` = model 1RE, number 001 (Eric, 2026-10-27).
 * The caller resolves it against the units it can see (`resolveUnitCodePartial`):
 * sequences are per model, continuous across suffixes and never reused
 * (ISS-037/039), so model + number names one unit.
 */
export interface UnitCodePartial {
  /** Model + padded number, e.g. `1RE001`: the key a caller resolves. */
  token: string;
  /** e.g. `1RE`. */
  model: string;
  /** Always 3 digits, e.g. `001`. */
  number: string;
}

export interface ParseUnitCodesOptions {
  /**
   * The unit codes the caller can see (often only the free ones). With them, a
   * word written after a code with a space counts as the suffix when it makes one
   * of these codes, or when it is a suffix in use (TCA, TIL, or one these codes
   * use) on the same line, so a booked unit ("2RE016 TIL") is still read as its
   * full code. Any other word ("1re1 and", "1re1 Aug 17") leaves the code as
   * shorthand, and after shorthand "til" is the word until ("1re1 til 8/20").
   */
  knownCodes?: string[];
}

export type CodeMatch =
  | { kind: 'full'; code: string }
  | { kind: 'partial'; partial: UnitCodePartial; tail: string }
  | { kind: 'none' };

/** The capture groups of one `buildUnitCodeRegex` match. */
export type CodeGroups = [
  model: string,
  separator: string | undefined,
  digits: string,
  suffixSeparator: string | undefined,
  suffix: string | undefined,
];

/** Sequences are 3 digits; a longer number written without a suffix is not a unit (`CAT2026`). */
const SHORTHAND_MAX_DIGITS = 3;

/** A number run straight on by a range or fraction (`1RE1-5`, `2re16/17`): likely several units or a date. */
const SHORTHAND_CONTINUATION = /^[-–—/]\d/;

/**
 * The suffixes in use today (TCA = California, TIL = Illinois). They count even
 * when no unit with them is in the caller's list (Sales passes only free units, so
 * a fully booked day lists none); any other suffix the caller's units use counts too.
 */
const SUFFIXES_IN_USE = ['TCA', 'TIL'];

/** Real suffixes that are also range words: after shorthand, "1re1 til 8/20" means until. */
const RANGE_WORD_SUFFIXES = new Set(['TIL']);

const STORED_CODE = /^([0-9A-Z]{3}\d{3})([A-Z]{2,4})$/;

interface KnownShape {
  /** The suffixes in use plus those the caller's units use. */
  suffixes: Set<string>;
  /** Model + number of every known unit, e.g. `1RE002`. */
  modelNumbers: Set<string>;
}
const shapeCache = new WeakMap<Set<string>, KnownShape>();

/** What a known-code set says about suffixes and numbers, worked out once per set. */
function shapeOf(known: Set<string>): KnownShape {
  let shape = shapeCache.get(known);
  if (!shape) {
    shape = { suffixes: new Set(SUFFIXES_IN_USE), modelNumbers: new Set() };
    for (const code of known) {
      const m = STORED_CODE.exec(code);
      if (!m) continue;
      shape.modelNumbers.add(m[1]);
      shape.suffixes.add(m[2]);
    }
    shapeCache.set(known, shape);
  }
  return shape;
}

/**
 * Whether a word written after a tight code with a space is that code's suffix.
 * It is when it makes a known code. Otherwise it must be a suffix in use, written
 * on the same line, not after a model and number a known unit already has (numbers
 * are never reused across suffixes, so "1RE002 til" is 1RE002TCA until ...), and,
 * after shorthand (1 or 2 digits), not a range word. A full 3-digit number names one
 * unit, so "2RE016 TIL" is that unit even when it is booked (missing from a
 * free-units list) or a date follows, while "1re1 til ..." is until.
 */
function spacedSuffixCounts(code: string, suffix: string, digits: string, separator: string, known: Set<string>): boolean {
  // A word on the next line is never the suffix, even when it would make a known
  // code: callers read the paste line by line, and the unit belongs to its own line.
  if (/[\r\n]/.test(separator)) return false;
  if (known.has(code)) return true;
  const shape = shapeOf(known);
  if (!shape.suffixes.has(suffix)) return false;
  if (shape.modelNumbers.has(code.slice(0, code.length - suffix.length))) return false;
  return digits.length >= SHORTHAND_MAX_DIGITS || !RANGE_WORD_SUFFIXES.has(suffix);
}

/**
 * The code shape is `{model}{sequence}{suffix}` but humans separate the parts with
 * spaces, dots, or hyphens, so `1RE 191 TCA` and `1RE-191-TCA` are the same unit,
 * and they drop the suffix or the zero padding (`1re1`, `1re1tca`).
 * Groups: 1 model, 2 separator, 3 digits, 4 separator, 5 suffix (optional).
 */
export function buildUnitCodeRegex(modelCodes: string[]): RegExp {
  return new RegExp('\\b(' + modelCodes.join('|') + ')([\\s.-]?)(\\d{1,4})(?:([\\s.-]?)([A-Z]{2,4}))?\\b', 'gi');
}

/**
 * What one regex match names: a full code, shorthand, or nothing. Shorthand
 * needs the model and number written together with nothing run straight on
 * after the number (`1re1`): model codes like OFF, CAT and MED are also words,
 * so "off 2 lights", "Cat-5 cable" and "OFF2/14" are not trailers.
 * `after` is the text right after the match.
 */
export function classifyCodeMatch(groups: CodeGroups, known: Set<string> | null, after: string): CodeMatch {
  const [rawModel, separator = '', digits, suffixSeparator = '', suffix] = groups;
  const model = rawModel.toUpperCase();
  const number = digits.padStart(3, '0');
  const shorthandOk =
    separator === '' && digits.length <= SHORTHAND_MAX_DIGITS && !(suffix ? false : SHORTHAND_CONTINUATION.test(after));
  const partial: UnitCodePartial = { token: model + number, model, number };

  if (suffix) {
    const upperSuffix = suffix.toUpperCase();
    const code = model + number + upperSuffix;
    const spaced = /\s/.test(suffixSeparator);
    if (spaced && known && shorthandOk && !spacedSuffixCounts(code, upperSuffix, digits, suffixSeparator, known)) {
      return { kind: 'partial', partial, tail: suffixSeparator + suffix };
    }
    return { kind: 'full', code };
  }
  if (shorthandOk) return { kind: 'partial', partial, tail: '' };
  return { kind: 'none' };
}

export function knownCodeSet(knownCodes?: string[]): Set<string> | null {
  return knownCodes ? new Set(knownCodes.map((c) => c.toUpperCase())) : null;
}

/** `String.replace` callback arguments → the capture groups of a code match. */
export function groupsOf(args: unknown[]): CodeGroups {
  return [args[1], args[2], args[3], args[4], args[5]] as CodeGroups;
}

/** `String.replace` callback arguments → the text right after the match. */
export function afterOf(args: unknown[]): string {
  const whole = args[0] as string;
  const offset = args[6] as number;
  const input = args[7] as string;
  return input.slice(offset + whole.length);
}

/**
 * Blank out codes and shorthand before date parsing, so their digits are never
 * read as dates. Only the code itself is blanked: a word written after it with a
 * space ("1re1 Aug 17", "1RE191 TCA") is kept, since it may be the date. A
 * code-shaped fragment that names no unit (`1RE1/2`, `CAT2026`) is blanked too,
 * so `1/2` inside it never becomes January 2.
 */
export function stripUnitCodes(text: string, modelCodes: string[], known: Set<string> | null): string {
  if (modelCodes.length === 0) return text;
  return text.replace(buildUnitCodeRegex(modelCodes), (...args: unknown[]) => {
    const groups = groupsOf(args);
    const [, separator = '', , suffixSeparator = '', suffix] = groups;
    const found = classifyCodeMatch(groups, known, afterOf(args));
    if (found.kind === 'partial') return ' ' + found.tail;
    if (found.kind === 'full') return suffix && /\s/.test(suffixSeparator) ? ' ' + suffixSeparator + suffix : ' ';
    return separator === '' ? ' ' : (args[0] as string);
  });
}
